import { useEffect, useMemo, useState } from 'react'

import { useQueries, useQuery } from '@tanstack/react-query'
import { isAfter, isWeekend } from 'date-fns'

import { getTMinus, getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Funds } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import { useFunds } from '@/react-query/queries/funds/funds'
import { queryKeys } from '@/react-query/queries/funds/keys'

const integerFormatter = numberFormatter({
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
  delimiter: true,
})

type SoftNavItem = Funds.GetSoftNavData[number]
type SoftNavBreakdownItem = Funds.GetSoftNavBreakdownData[number]
type SoftNavPnLItem = Funds.GetSoftNavPnLData[number]

const pnlColDefs = createColDefs<SoftNavPnLItem>([
  {
    field: 'fund_name',
    headerName: 'Fund',
    width: 125,
    cellStyle: { textAlign: 'center' },
  },

  {
    field: 'unique_identifier',
    headerName: 'Instrument',
    width: 125,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'reference_instrument_unique_identifier',
    headerName: 'Ref Instrument',
    width: 125,
    cellStyle: { textAlign: 'center' },
  },

  {
    field: 'instrument_type',
    headerName: 'Type',
    width: 110,
    cellStyle: { textAlign: 'center' },
  },

  {
    field: 'quantity',
    headerName: 'Quantity',
    filter: false,
    width: 130,
    cellStyle: { textAlign: 'right' },
    valueFormatter: numberFormatter({ delimiter: true }),
  },
  {
    field: 'price',
    headerName: 'Price',
    filter: false,
    width: 130,
    cellStyle: { textAlign: 'right' },
    valueFormatter: numberFormatter({
      delimiter: true,
      maximumFractionDigits: 5,
      minimumFractionDigits: 5,
    }),
  },

  {
    headerName: 'Currencies',
    groupId: 'Currencies',
    children: [
      {
        field: 'trade_currency',
        headerName: 'Trade',
        width: 90,
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'inst_currency',
        headerName: 'Inst',
        width: 90,
        cellStyle: { textAlign: 'center' },
      },
      {
        field: 'fund_currency',
        headerName: 'Fund',
        width: 90,
        cellStyle: { textAlign: 'center' },
      },
    ],
  },

  {
    field: 'trade_datetime_utc',
    headerName: 'Trade DT',
    width: 165,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'settlement_date',
    headerName: 'Sett DT',
    width: 110,
    cellStyle: { textAlign: 'center' },
  },

  {
    field: 'top_level_bucket',
    headerName: 'Top Lvl',
    width: 115,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'investment_bucket',
    headerName: 'Inv Type',
    width: 115,
    cellStyle: { textAlign: 'center' },
  },

  {
    headerName: 'Prices & Fx',
    groupId: 'Prices & Fx',
    children: [
      {
        field: 'px_t',
        headerName: 'Px(T)',
        filter: false,
        width: 110,
        cellStyle: { textAlign: 'right' },
        valueFormatter: numberFormatter({
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
          delimiter: true,
        }),
      },
      {
        field: 'px_tmo',
        headerName: 'Px(T-1)',
        filter: false,
        width: 100,
        cellStyle: { textAlign: 'right' },
        valueFormatter: numberFormatter({
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
          delimiter: true,
        }),
      },

      {
        field: 'fx_rate_t',
        headerName: 'Fx(T)',
        filter: false,
        width: 125,
        cellStyle: { textAlign: 'center' },
        valueFormatter: numberFormatter({
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
          delimiter: true,
        }),
      },
      {
        field: 'fx_rate_tmo',
        headerName: 'Fx(T-1)',
        filter: false,
        width: 125,
        cellStyle: { textAlign: 'center' },
        valueFormatter: numberFormatter({
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
          delimiter: true,
        }),
      },
    ],
  },

  {
    field: 'pnl',
    headerName: 'P&L',
    filter: false,
    width: 125,
    cellStyle: { textAlign: 'right' },
    valueFormatter: integerFormatter,
  },
])

const breakdownColDefs = createColDefs<SoftNavBreakdownItem>([
  {
    field: 'pnl_type',
    headerName: 'Group',
    width: 150,
    cellStyle: { textAlign: 'center' },
    rowGroup: true,
    hide: true,
  },
  {
    field: 'unique_identifier',
    headerName: 'Instrument',
    width: 250,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'value',
    headerName: 'Value',
    width: 150,
    cellStyle: { textAlign: 'right' },
    valueFormatter: integerFormatter,
  },
])

const softNavColDefs = createColDefs<SoftNavItem>([
  { field: 'value_date', headerName: 'Date', width: 125, cellStyle: { textAlign: 'center' } },
  {
    field: 'fund_name',
    headerName: 'Fund',
    width: 125,
    cellStyle: { textAlign: 'center' },
  },

  {
    headerName: 'Soft NAV (Base)',
    groupId: 'Soft NAV (Base)',
    children: [
      {
        field: 'tech_soft_nav',
        headerName: 'Tech',
        filter: false,
        width: 125,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
      {
        field: 'ops_soft_nav',
        headerName: 'Ops',
        filter: false,
        width: 125,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
      {
        field: 'soft_nav_discr',
        headerName: 'Δ',
        filter: false,
        width: 90,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
      {
        field: 'soft_nav_discr_bps',
        headerName: 'Δ bps',
        filter: false,
        width: 90,
        cellStyle: { textAlign: 'center' },
        valueFormatter: numberFormatter({
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
          suffix: ' bp',
        }),
        // valueFormatter: (params) => floatFormatterSuffix(params, "soft_nav_discr_bps", " bp", 2),
      },
    ],
  },

  {
    headerName: 'Positions P&L (Base)',
    groupId: 'Positions P& (Base)L',
    children: [
      {
        field: 'tech_pnl',
        headerName: 'Tech',
        filter: false,
        width: 110,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
      {
        field: 'ops_pnl',
        headerName: 'Ops',
        filter: false,
        width: 110,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
      {
        field: 'pnl_discr',
        headerName: 'Δ',
        filter: false,
        width: 90,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
    ],
  },

  {
    headerName: 'Deals (Base)',
    groupId: 'Deals (Base)',
    children: [
      {
        field: 'tech_deals',
        headerName: 'Tech',
        filter: false,
        width: 110,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
      {
        field: 'ops_deals',
        headerName: 'Ops',
        filter: false,
        width: 110,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
      {
        field: 'deals_discr',
        headerName: 'Δ',
        filter: false,
        width: 90,
        cellStyle: { textAlign: 'right' },
        valueFormatter: integerFormatter,
      },
    ],
  },
])

export interface AutomatedNavPageProps {}

export const AutomatedNavPage: React.FC<AutomatedNavPageProps> = ({}) => {
  const [fund_name, setFundName] = useState<string | undefined>()
  const [value_date, setValueDate] = useState(getTMinus1())
  const [start_date, setStartDate] = useState(getTMinus(2))

  const api = useApi()

  const params = {
    value_date: toISO8601(value_date),
    value_date_mo: toISO8601(start_date),
  }

  const { data: funds, query: fundsQuery } = useFunds()

  useEffect(() => {
    if (funds.length && !fund_name) {
      setFundName(funds?.[0].fund_name)
    }
  }, [funds])

  const paramsWithFundName = {
    ...params,
    fund_name: fund_name!,
  }

  const softNavQuery = useQuery({
    queryKey: queryKeys.softNav(params),
    queryFn: () => api.funds.nav.getSoftNav(params),
  })

  const softNavData = useMemo(() => {
    return softNavQuery.data ?? []
  }, [softNavQuery.data])

  const dateDisabled = (date: Date) => {
    return isAfter(date, new Date()) || isWeekend(date)
  }

  const dependentQueries = useQueries({
    queries: [
      {
        queryKey: queryKeys.softNavBreakdown(paramsWithFundName),
        queryFn: () => api.funds.nav.getSoftBreakdown(paramsWithFundName),
        enabled: !!fund_name,
      },
      {
        queryKey: queryKeys.softNavPnL(paramsWithFundName),
        queryFn: () => api.funds.nav.getSoftPnL(paramsWithFundName),
        enabled: !!fund_name,
      },
    ],
  })

  const [breakdownQuery, pnlQuery] = dependentQueries

  const queriesAreFetching = [fundsQuery, softNavQuery, ...dependentQueries].some(
    (query) => query.isFetching,
  )
  const inputsDisabled = queriesAreFetching

  return (
    <Page
      metadata={{ title: 'Automated NAV' }}
      permissions={OPERATIONS.DB_REC.AUTOMATED_NAV.permissions}
    >
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <FormItem>
            <Label className={'text-xs'}>Value Date</Label>
            <DatePicker
              size="sm"
              selected={value_date}
              onSelect={(date) => date && setValueDate(date)}
              buttonDisabled={inputsDisabled}
              disabled={dateDisabled}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-xs'}>Start Date</Label>
            <DatePicker
              size="sm"
              selected={start_date}
              onSelect={(date) => date && setStartDate(date)}
              buttonDisabled={inputsDisabled}
              disabled={dateDisabled}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-xs'}>Fund</Label>
            <SearchSelect
              size="sm"
              value={fund_name}
              loading={fundsQuery.isFetching}
              placeholder={'Select a fund...'}
              setValue={setFundName}
              className={'min-w-auto w-40'}
              disabled={inputsDisabled}
              options={funds.map((fund) => ({ label: fund.fund_name, value: fund.fund_name }))}
            />
          </FormItem>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'horizontal'} autoSaveId={'conditional'}>
        <ResizablePanel order={1} id="main-panel" minSize={5}>
          <ResizablePanelGroup direction={'vertical'} autoSaveId={'conditional'}>
            <ResizablePanel order={1} id="soft-nav-panel" minSize={5}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery query={softNavQuery} title="Funds Soft Nav" />
                <WidgetContent loading={softNavQuery.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    rowData={softNavData}
                    columnDefs={softNavColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel order={2} id="pnl-panel" minSize={5}>
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery
                  query={pnlQuery}
                  title={`${fund_name ? `${fund_name} - ` : ''}P&L Details`}
                  actionComponent={<>sdf</>}
                />
                <WidgetContent loading={pnlQuery.isLoading || fundsQuery.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    rowData={pnlQuery.data ?? []}
                    columnDefs={pnlColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel defaultSize={30} order={2} minSize={5} id="breakdown-panel">
          <Widget exposed className={'h-full'}>
            <WidgetHeaderWithQuery
              query={breakdownQuery}
              title={`${fund_name ? `${fund_name} - ` : ''}Soft NAV Breakdown`}
            />
            <WidgetContent loading={breakdownQuery.isLoading || fundsQuery.isLoading}>
              <CustomAgGrid
                domLayout={'normal'}
                rowData={breakdownQuery.data ?? []}
                columnDefs={breakdownColDefs}
              />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
